/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: json-viewer-component.ts
 *
 * Created by randysorensen on 2019-01-28.
 */

function JsonViewerController (
    this: JS5Object,
    _: JS5Object,
    uiGridConstants: JS5Object
) {
    const parseJsonData = (gridData: JS5Object[], node: any, treeLevel: number): void => {
        _.forEach(node, (value: any, propertyName: any): void => {
            let gridRow: JS5Object = {
                property: `${propertyName}`,
                columnsContainingSearchResult: [],
                index: this.indexSeq,
                $$treeLevel: treeLevel
            };

            this.indexSeq++;

            if (typeof value === 'object') {
                gridRow.value = '';
                gridData.push(gridRow);
                parseJsonData(gridData, value, treeLevel + 1);
            } else {
                gridRow.value = `${value}`;
                gridData.push(gridRow);
            }
        });
    };

    const updateSearchResults = () => {
        this.searchResultIndex = 0;
        this.searchResultRows = [];

        _.forEach(this.gridOptions.data, (rowEntity: JS5Object) => {
            rowEntity.columnsContainingSearchResult = [];

            let rowMatchesSearchText = false;
            _.forEach(this.gridOptions.columnDefs, (columnDef: JS5Object) => {
                let lowerCaseValue = _.toLower(rowEntity[columnDef.field]);

                if (_.size(this.searchText) && _.includes(lowerCaseValue, this.searchText)) {
                    rowMatchesSearchText = true;
                    rowEntity.columnsContainingSearchResult.push(columnDef.field);
                }
            });

            if (rowMatchesSearchText) {
                this.searchResultRows.push(rowEntity);
            }
        });

        this.setCurrentSearchResultIndex(this.searchResultIndex + 1);
        this.setSearchResultTotal(this.searchResultRows.length);
    };

    const transformScrollToRowNumber = (rowNumber: number) => {
        let gridDiv = document.getElementById('jsonViewerGrid');
        let gridHeightInRows = Math.floor(gridDiv.clientHeight / this.gridOptions.rowHeight);
        let destinationRow = rowNumber + gridHeightInRows;
        return Math.min(destinationRow - 1, this.gridOptions.data.length - 1);
    };

    const goToCurrentSearchResult = () => {
        let resultNumber = this.searchResultIndex;
        let resultRow = this.searchResultRows[resultNumber];
        if (_.isNil(resultRow)) {
            // scroll to top if there's no search results
            resultRow = this.gridOptions.data[0];
        }

        let resultRowIndex = resultRow.index;

        if (this.scrollDirection > 0) {
            // advance the current index enough to make the result appear on top
            let scrollToIndex = transformScrollToRowNumber(resultRowIndex);
            resultRow = this.gridOptions.data[scrollToIndex];
        }

        // this.gridApi.core.scrollTo(_.first(this.gridOptions.data[0]))
       this.gridApi.core.scrollTo(resultRow).then(() => {
            this.setCurrentSearchResultIndex(resultNumber + 1);
            this.gridApi.core.refresh();
        });
    };


    //==========================//
    // Search Result Navigation //
    //==========================//

    this.findNext = () => {
        this.searchResultIndex++;
        if (this.searchResultIndex >= this.searchResultRows.length) {
            this.searchResultIndex = 0;
            this.scrollDirection = -1;
        } else {
            this.scrollDirection = 1;
        }

        goToCurrentSearchResult();
    };

    this.findPrevious = () => {
        this.searchResultIndex--;
        if (this.searchResultIndex < 0) {
            this.searchResultIndex = this.searchResultRows.length - 1;
            this.scrollDirection = 1;
        } else {
            this.scrollDirection = -1;
        }

        goToCurrentSearchResult();
    };


    //=================================//
    // Result Tally Message Generators //
    //=================================//

    this.getSearchResultCountString = (): string => {
        const currentIndex = this.currentSearchResultIndex;
        const totalResults = this.searchResultTotal;

        if (_.size(this.searchText) && this.searchResultTotal > 0) {
            return `${currentIndex} of ${totalResults}`;
        } else {
            return '';
        }
    };

    this.getNoResultsMsg = (): string => {
        if (_.size(this.searchText) && this.searchResultTotal === 0) {
            return `${this.searchText} not found.`
        } else {
            return '';
        }
    };


    //================//
    // Event Handlers //
    //================//

    this.onSearchTextUpdated = (searchText: string) => {
        let searchTextLowerCase = _.toLower(searchText);
        if (searchTextLowerCase !== this.searchText) {
            this.searchText = searchTextLowerCase;
            updateSearchResults();
            goToCurrentSearchResult();
        } else {
            this.findNext();
        }
    };

    this.onKeyUp = ($event: any) => {
        if ($event.key === 'Enter') {
            this.findNext();
        }
    };

    //
    // This facilitates "search as you type" when using the native
    // search.
    //
    this.onSearchTextInputChanged = () => {
        this.onSearchTextUpdated(this.searchTextInput);
    };


    //======//
    // Grid //
    //======//

    this.cellHasASearchResult = (rowEntity: JS5Object, colFieldName: string): boolean => {
        return _.includes(rowEntity.columnsContainingSearchResult, colFieldName);
    };

    this.cellHasCurrentSearchResult = (rowEntity: JS5Object, colFieldName: string): boolean => {
        return (
            _.includes(rowEntity.columnsContainingSearchResult, colFieldName) &&
            this.searchResultRows[this.searchResultIndex] === rowEntity
        );
    };

    const initGrid = () => {
        this.gridOptions = {
            enableSorting: false,
            enableFiltering: false,
            enableColumnMenus: false,
            showTreeExpandNoChildren: false,
            treeRowHeaderBaseWidth: 12,
            enableHorizontalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
            enableVerticalScrollbar: uiGridConstants.scrollbars.ALWAYS,
            rowHeight: 26,
            showHeader: false,
            rowTemplate: 'components/visual/grid/searchview-component-row-template.html',
            onRegisterApi: (gridApi: JS5Object) => {
                this.gridApi = gridApi;
                this.gridApi.grid.registerDataChangeCallback(() => {
                    this.gridApi.treeBase.expandAllRows();
                });
            },
            data: []
        };

        this.gridOptions.columnDefs = [
            {
                field: 'property',
                type: 'string',
                width: '25%',
                displayName: 'Property',
                cellTemplate: 'components/jsonViewer/gridTemplates/json-property-cell-template.html'
            },
            {
                field: 'value',
                type: 'string',
                width: '*',
                displayName: 'Value',
                cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html'
            }];
    };


    //=================//
    // Lifecycle Hooks //
    //=================//

    this.$postLink = () => {
        this.jsonDataPromise.then((jsonData: JS5Object) => {
            this.gridOptions.data = [];
            parseJsonData(this.gridOptions.data, jsonData, 0);
        });
    };

    this.$onInit = () => {
        this.searchText = '';
        this.searchTextInput = '';
        this.searchResultIndex = 0;
        this.indexSeq = 0;
        this.scrollDirection = 1;
        this.searchResultRows = [];

        if (this.useNativeSearch) {
            this.setSearchResultTotal = (searchResultTotal: number) => {
                this.searchResultTotal = searchResultTotal;
            };

            this.setCurrentSearchResultIndex = (currentSearchResultIndex: number) => {
                this.currentSearchResultIndex = currentSearchResultIndex;
            };
        } else {
            this.setSearchCallback(this.onSearchTextUpdated);
            this.setFindNextCallback(this.findNext);
            this.setFindPreviousCallback(this.findPrevious);
            this.setShowNoResultsMsg(true);
        }

        this.setSearchResultTotal(0);
        this.setCurrentSearchResultIndex(0);

        initGrid();
    };

    this.$onDestroy = () => {
        if (!this.useNativeSearch) {
            this.setSearchCallback(null);
            this.setFindNextCallback(null);
            this.setFindPreviousCallback(null);
            this.setSearchResultTotal(0);
            this.setCurrentSearchResultIndex(0);
            this.setShowNoResultsMsg(false);
        }
    };

}


angular.module('prism-ui.components').component('jsonViewer', {
    controller: JsonViewerController,
    templateUrl: 'components/jsonViewer/json-viewer-template.html',
    bindings: {

        // promise that resolves with the json data to display
        jsonDataPromise: '<',

        // "true" if not using the search controls on the app's
        // navbar, in which case all bindings below will be ignored
        useNativeSearch: '<',


        //========================//
        // Navbar Search Bindings //
        //========================//

        // called during $onInit() to expose our component's
        // "execute search" function
        setSearchCallback: '<',

        // called during $onInit() to expose our "find next"
        // function
        setFindNextCallback: '<',

        // called during $onInit() to expose our "find previous"
        // function
        setFindPreviousCallback: '<',

        // called when the total number of search results changes
        // based on search keyword changes
        setSearchResultTotal: '<',

        // called when the current search result index is changed
        // (i.e. by the "find next" action)
        setCurrentSearchResultIndex: '<',

        // called to explicitly tell the navbar (or other external
        // search component) to turn off the "... not found"
        // message
        setShowNoResultsMsg: '<'
    }
});
